import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getTraces, type TraceSummary, type GetTracesParams } from "../api/client";
import type { ServiceSlug } from "../api/client";
import type { TimeRange } from "../utils/time";

export interface UseTracesOptions {
  serviceSlug: ServiceSlug | null;
  timeRange: TimeRange;
  serviceNames?: string[];
  serviceInstances?: string[];
  traceIds?: string[];
  minDurationMs?: number | null;
  maxDurationMs?: number | null;
  minSpanCount?: number | null;
  maxSpanCount?: number | null;
  sort?: string;
  order?: string;
  limit?: number;
  refreshToken?: number;
  enabled?: boolean;
}

export interface UseTracesResult {
  traces: TraceSummary[];
  loading: boolean;
  isFetching: boolean;
  error: string | null;
}

/**
 * Hook for fetching trace summaries with filters.
 *
 * @param options - Configuration options for fetching traces
 * @returns Object containing traces, loading state, and error
 */
export function useTraces({
  serviceSlug,
  timeRange,
  serviceNames = [],
  serviceInstances = [],
  traceIds = [],
  minDurationMs = null,
  maxDurationMs = null,
  minSpanCount = null,
  maxSpanCount = null,
  sort,
  order,
  limit,
  refreshToken = 0,
  enabled = true,
}: UseTracesOptions): UseTracesResult {
  const serviceNamesKey = serviceNames.join(",");
  const serviceInstancesKey = serviceInstances.join(",");
  const traceIdsKey = traceIds.join(",");

  const query = useQuery({
    queryKey: [
      "traces",
      serviceSlug,
      timeRange.from.toISOString(),
      timeRange.to.toISOString(),
      serviceNamesKey,
      serviceInstancesKey,
      traceIdsKey,
      minDurationMs ?? null,
      maxDurationMs ?? null,
      minSpanCount ?? null,
      maxSpanCount ?? null,
      sort ?? null,
      order ?? null,
      limit ?? null,
      refreshToken,
    ],
    enabled: !!serviceSlug && enabled,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const params: GetTracesParams = {
        serviceSlug: serviceSlug!,
        startTime: timeRange.from,
        endTime: timeRange.to,
        serviceNames,
        serviceInstances,
        traceIds,
        minDurationMs: minDurationMs,
        maxDurationMs: maxDurationMs,
        minSpanCount: minSpanCount,
        maxSpanCount: maxSpanCount,
        sort,
        order,
        limit,
        signal,
      };
      const results = await getTraces(params);
      return results.slice(0, limit ?? 100);
    },
  });

  if (!serviceSlug || !enabled) {
    return { traces: [], loading: false, isFetching: false, error: null };
  }

  const error =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? "Failed to load traces"
        : null;
  return {
    traces: query.data ?? [],
    loading: query.isLoading,
    isFetching: query.isFetching,
    error,
  };
}
